import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { MainHeadline, SmallHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { SearchForm } from 'components/Form'
import { BlueButton, OrangeButton, TextButton } from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { getStoreRef } from 'reducks/auth/selectors'
import { State } from 'index'
import { getStoreTimesale } from 'reducks/storeTimesale/selectors'
import { getBreads } from 'reducks/bread/selectors'
import { fetchBreads } from 'reducks/bread/operations'
import {
  useBreadTypeFilter,
  useHandleKeyword,
  useInitBreadList,
  useSearchBreads,
  useSelectBreadsByType,
} from 'hooks/bread'
import { BreadType } from 'types/bread'
import { getCategories } from 'reducks/category/selectors'
import { getStocks } from 'reducks/stock/selectors'
import { fetchFutureStocks } from 'reducks/stock/operations'
import { FlexRow } from 'components/Flex'
import { TimeSaleProductCardList } from 'components/Card'
import { StoreTimesale } from 'types/storeTimesale'
import { saveStoreTimesaleBreads } from 'reducks/storeTimesale/operations'
import { StockUnionType } from 'types/stock'

const categoryName = ''
const isShownPrivate = false
// TODO 将来的に注文タイプ別に設定できるようにする
const type: StockUnionType = 'daily'

const TimeSaleTemplate: FC = () => {
  const dispatch = useDispatch()
  const breads: BreadType[] = useSelector((state: State) => getBreads(state))
  const categories = useSelector((state: State) => getCategories(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeTimesale: StoreTimesale = useSelector((state: State) => getStoreTimesale(state))
  const stocks = useSelector((state: State) => getStocks(state))

  const [keyword, setKeyword] = useState<string>('')
  // TODO 将来的に注文タイプ別に設定できるようにする
  const { breadTypes } = useBreadTypeFilter([type])
  const [breadList, setBreadList] = useState<BreadType[]>([])
  const {
    selectBreadByType,
    selectedBreadsByType,
    selectAllBreadsByType,
    setSelectedBreadsByType,
    unselectAllBreadsByType,
  } = useSelectBreadsByType(breadList)
  // 検索キーワードの入力
  const handleKeyword = useHandleKeyword({
    breads,
    categories,
    categoryName,
    setBreadList,
    setKeyword,
    isShownPrivate,
    breadTypes,
  })
  // キーワード検索(部分一致)
  const searchBreads = useSearchBreads({
    breads,
    categories,
    categoryName,
    setBreadList,
    isShownPrivate,
    breadTypes,
  })
  // Reduxで取得したstocksをローカルのbreadListに反映する
  useInitBreadList({
    breads,
    categories,
    categoryName,
    keyword,
    setBreadList,
    isShownPrivate,
    breadTypes,
  })

  const goBackToTimeSale = useCallback(() => dispatch(push(`/time_sale`)), [dispatch])

  const saveSelectedBreads = useCallback(() => {
    dispatch(saveStoreTimesaleBreads(storeRef, selectedBreadsByType))
  }, [dispatch, storeRef, selectedBreadsByType])

  // 商品情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'public'))
  }, [storeRef])

  // 未来の在庫情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchFutureStocks({ storeRef, types: ['preorder'] }))
  }, [storeRef])

  // selectedBreadsByTypeを設定
  useEffect(() => {
    if (breads.length === 0) return
    if (storeTimesale) {
      setSelectedBreadsByType((prevState) => {
        storeTimesale.breads.forEach((_bread) => {
          const bread = breads.find((bread) => bread.bread_ref?.id === _bread.bread_id)
          if (!bread) return
          _bread.types.forEach((type) => {
            // すでに追加されている場合は追加しない
            if (prevState[type].some((bread) => bread.bread_ref?.id === _bread.bread_id)) return
            prevState[type].push(bread)
          })
        })
        return { ...prevState }
      })
    }
  }, [breads, storeTimesale, type])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'セール対象商品を選択'} />
      <Spacer size={20} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => searchBreads(keyword)}
        placeholder={'商品の名前で検索'}
        value={keyword}
      />
      <Spacer size={12} />
      {/* TODO 将来的に注文タイプ別にフィルターできるようにする
      <FilterLabel label={'注文タイプ：'} />
      <Spacer size={8} />
      <SubDescription description={'※セール対象の商品点数は（）で表示されています。'} />
      <Spacer size={8} />
      <FlexRow justifyContent={'start'} wrap={'wrap'}>
        {BREAD_TYPES.map((BREAD_TYPE) => {
          const { id, name } = BREAD_TYPE
          const amount = breads.reduce((prev: number, bread: BreadType) => {
            if (id === 'daily' && bread.is_type_daily) {
              return prev + 1
            } else if (id === 'preorder' && bread.is_type_preorder) {
              return prev + 1
            } else if (id === 'delivery' && bread.is_type_delivery) {
              return prev + 1
            } else {
              return prev
            }
          }, 0)
          return (
            <TagCheckbox
              key={id}
              hasHashtag={false}
              id={id}
              isChecked={!isAllBreadTypesSelected && breadTypes.some((type) => type === id)}
              onChange={handleBreadTypes}
              type={'outlined'}
              value={`${name}（${amount}）`}
            />
          )
        })}
        <TagCheckbox
          hasHashtag={false}
          id={'all'}
          isChecked={isAllBreadTypesSelected}
          onChange={selectAllBreadTypes}
          type={'outlined'}
          value={`すべて（${breads.length}）`}
        />
      </FlexRow>
      <Spacer size={20} />
        */}
      <SmallHeadline label={`${breadList.length}件の商品を表示中`} />
      <Spacer size={36} />
      <FlexRow justifyContent={'end'}>
        {selectedBreadsByType[type].filter((selectedBread) =>
          breadList.some((bread) => bread.bread_ref?.id === selectedBread.bread_ref?.id)
        ).length > 0 ? (
          <TextButton label={'すべての商品のチェックを外す'} onClick={() => unselectAllBreadsByType(type)} />
        ) : (
          <TextButton label={'すべての商品にチェックを入れる'} onClick={() => selectAllBreadsByType(type)} />
        )}
      </FlexRow>
      <Spacer size={12} />
      <TimeSaleProductCardList
        breads={breadList}
        selectedBreads={selectedBreadsByType[type]}
        selectBread={(e) => selectBreadByType(e, type)}
        stocks={stocks}
      />
      <Spacer size={48} />
      <OrangeButton label={'セール対象商品を保存する'} onClick={saveSelectedBreads} size={'medium'} type={'main'} />
      <Spacer size={16} />
      <BlueButton
        endIconPath={'/icon/white/arrow_next_small.svg'}
        label={'タイムセールへ戻る'}
        onClick={goBackToTimeSale}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={72} />
    </div>
  )
}
export default TimeSaleTemplate
